import { GlobeAltIcon, PlusIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { useAppDispatch } from "hooks/hooks";
import { FC, useState } from "react";
import { Helmet } from "react-helmet";
import { SubmitHandler } from "react-hook-form";
import { createContactRequest } from "redux/features/contact/contactSlice";
import { FaEnvelope, FaClock, FaQuestionCircle, FaComments, FaStar } from "react-icons/fa";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";

import TestimonialCards from "components/Testimonial";
import { CONTACT_FORM_FAQ, CONTACT_FORM_INFORMATIONS } from "data/constants";
import { ContactFormData } from "data/schemas";
import toast from "react-hot-toast";
import SocialsList from "shared/SocialsList/SocialsList";
import { socket } from "../../utils/socket";
import ContactForm from "./ContactForm";
import { EASING } from "../../utils/easingUtils";

export interface PageContactProps {
  className?: string;
}

const PageContact: FC<PageContactProps> = ({ className = "" }) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const dispatch = useAppDispatch();

  const toggleFAQ = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const answerVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 },
  };

  const onSubmit: SubmitHandler<ContactFormData> = async (data) => {
    try {
      const res = await dispatch(createContactRequest(data)).unwrap();
      if (res.status === "ok") {
        toast.success(res.message);
        socket.emit("send-notification", {
          action: "send_contact_request",
          email: data.email,
        });
      }
    } catch (error: any) {
      console.error("Submission error:", error);
      toast.error(error.message || "Échec de l'envoi du message.");
    }
  };

  return (
    <div className={`nc-PageContact relative overflow-hidden ${className}`} data-nc-id="PageContact">
      <Helmet>
        <title>Espaces à louer à Paris, Ile de France et partout en France | Event To Bee</title>
        <meta
          name="description"
          content="Vous cherchez ou avez des espaces à louer ? Contactez-nous pour en discuter ! Réponse rapide garantie !"
        />
      </Helmet>

      <div className="relative py-16 lg:py-24 bg-gradient-to-br from-pink-50 via-white to-rose-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900">
        {/* Background Effects */}
        <BgGlassmorphism />
        <div className="absolute -top-6 -left-6 w-72 h-72 bg-gradient-to-br from-pink-200/30 to-rose-200/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-6 -right-6 w-96 h-96 bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full blur-3xl"></div>

        <div className="container relative px-4 mx-auto max-w-7xl">
          {/* Hero Section */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-pink-50 to-rose-50 rounded-full text-sm font-medium text-pink-600 mb-6">
              <FaEnvelope className="w-4 h-4 mr-2 text-pink-500" />
              Contactez-nous
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-neutral-900 dark:text-white mb-6">
              Contactez-nous pour toute demande concernant nos espaces événementiels
            </h2>
            <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-4xl mx-auto leading-relaxed">
              Une question sur une réservation ? Besoin d'un renseignement sur un espace événementiel ? Nous sommes
              là pour répondre à vos questions sur les espaces à louer à Paris, Île-de-France et partout en France.
            </p>

            {/* Response Time Badge */}
            <div className="mt-8 inline-flex items-center bg-white dark:bg-neutral-800 rounded-full px-6 py-3 shadow-lg">
              <FaClock className="w-5 h-5 text-pink-500 mr-2" />
              <span className="text-neutral-700 dark:text-neutral-300 font-medium">
                Réponse sous <span className="font-bold text-pink-600 dark:text-pink-400">24h</span> garantie !
              </span>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="space-y-8"
            >
              <div className="bg-white dark:bg-neutral-800 rounded-3xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-neutral-900 dark:text-white mb-8 flex items-center">
                  <FaComments className="w-6 h-6 mr-3 text-pink-500" />
                  Comment nous contacter ?
                </h3>

                <div className="space-y-6">
                  {CONTACT_FORM_INFORMATIONS.map((item, index) => (
                    <motion.div
                      key={index}
                      className="group p-4 rounded-2xl hover:bg-pink-50 dark:hover:bg-neutral-700 transition-colors duration-300"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1 }}
                    >
                      <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-200 mb-2 flex items-center">
                        <span className="w-10 h-10 bg-pink-100 dark:bg-pink-900/30 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                          <span className="text-pink-600 dark:text-pink-400">{item.icon}</span>
                        </span>
                        {item.title}
                      </h4>
                      <p className="text-neutral-600 dark:text-neutral-400 ml-14">{item.desc}</p>
                    </motion.div>
                  ))}

                  <motion.div
                    className="group p-4 rounded-2xl hover:bg-pink-50 dark:hover:bg-neutral-700 transition-colors duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                  >
                    <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-200 mb-4 flex items-center">
                      <span className="w-10 h-10 bg-pink-100 dark:bg-pink-900/30 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                        <GlobeAltIcon className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                      </span>
                      Réseaux sociaux
                    </h4>
                    <div className="ml-14">
                      <SocialsList className="flex gap-4" />
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <ContactForm onSubmit={onSubmit} />
          </div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mt-20"
          >
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-pink-50 to-rose-50 rounded-full text-sm font-medium text-pink-600 mb-6">
                <FaQuestionCircle className="w-4 h-4 mr-2 text-pink-500" />
                Questions fréquentes
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white">
                Espaces à louer : FAQ
              </h3>
            </div>

            <div className="max-w-4xl mx-auto space-y-4">
              {CONTACT_FORM_FAQ.map((faq, index) => (
                <motion.div
                  key={index}
                  className="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                >
                  <button
                    className="flex items-center justify-between w-full p-6 text-left transition-all duration-300 hover:bg-pink-50 dark:hover:bg-neutral-700"
                    onClick={() => toggleFAQ(index)}
                  >
                    <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-200 pr-4">
                      {faq.question}
                    </h4>
                    <motion.div
                      animate={{ rotate: expandedIndex === index ? 45 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full shadow-lg flex-shrink-0 pointer-events-none"
                    >
                      <PlusIcon className="w-5 h-5" />
                    </motion.div>
                  </button>
                  <AnimatePresence>
                    {expandedIndex === index && (
                      <motion.div
                        layout
                        variants={answerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        transition={{ duration: 0.4, ease: EASING.easeInOut }}
                        className="px-6 pb-6 text-neutral-600 dark:text-neutral-400 border-t border-neutral-200 dark:border-neutral-700"
                      >
                        <div className="pt-4" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="bg-neutral-50 dark:bg-neutral-800">
        <TestimonialCards title="Avis clients" />
      </div>

      {/* Final CTA Section */}
      <div className="relative py-16 bg-gradient-to-br from-pink-50 via-white to-rose-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900">
        <div className="absolute -top-6 -left-6 w-72 h-72 bg-gradient-to-br from-pink-200/20 to-rose-200/20 rounded-full blur-3xl"></div>
        <div className="container relative px-4 mx-auto max-w-7xl">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <div className="bg-gradient-to-r from-pink-500 via-pink-600 to-rose-500 rounded-3xl p-8 text-white max-w-4xl mx-auto">
              <div className="flex items-center justify-center mb-4">
                <FaStar className="w-6 h-6 mr-2" />
                <span className="text-lg font-semibold">Trouvons ensemble le lieu parfait</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Vous cherchez l'espace idéal pour votre prochain événement ?
              </h3>
              <p className="text-lg mb-6 opacity-90">
                Que vous ayez un espace à louer ou que vous cherchiez un lieu pour organiser votre événement,
                contactez-nous dès maintenant !
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/add/location"
                  className="bg-white text-pink-600 px-8 py-4 rounded-full font-semibold hover:scale-105 transform transition-all duration-300 shadow-lg text-center cursor-pointer"
                >
                  Proposer un espace
                </a>
                <a
                  href="/espace-evenementiel"
                  className="bg-pink-700 text-white px-8 py-4 rounded-full font-semibold hover:scale-105 transform transition-all duration-300 border-2 border-white/20 text-center cursor-pointer"
                >
                  Chercher un lieu
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PageContact;
